import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr, TypeUtils } from '@utils';
import { useService } from '@hooks';
import { AccessType, CoverageAndAccessibility, InstallationType, ParkArea, siteService } from '@services';
import { Grid, MenuItem, Stack } from '@mui/material';
import { FormikNumber, FormikRadio, FormikRadioGroup, FormikTextfield } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';

type FormFields = Extendable<{
	siteId: string;
	name: string;
	access?: AccessType;
	coverageAndAccessibility?: CoverageAndAccessibility;
	installationType?: InstallationType;
	floorInstallation?: string;
	powerAvailableForCharging?: number;
	parkingSlotsNumber: number;
	currentACInstalled?: number;
	currentDCInstalled?: number;
}>;

const getAccessTypeText = (inp: AccessType): ReactNode => {
	if (inp === AccessType.LIMITED) {
		return <Tr.Portal path="parking-area.access-type-limited" />;
	} else if (inp === AccessType.OPEN_ACCESS) {
		return <Tr.Portal path="parking-area.access-type-open-access" />;
	} else {
		return inp;
	}
};
const getCoverageAndAccessibilityText = (inp: CoverageAndAccessibility): ReactNode => {
	if (inp === CoverageAndAccessibility.BASEMENT) {
		return <Tr.Portal path="parking-area.coverage-and-accessibility-basement" />;
	} else if (inp === CoverageAndAccessibility.GARAGE) {
		return <Tr.Portal path="parking-area.coverage-and-accessibility-garage" />;
	} else if (inp === CoverageAndAccessibility.MULTIFLOOR) {
		return <Tr.Portal path="parking-area.coverage-and-accessibility-multifloor" />;
	} else if (inp === CoverageAndAccessibility.OPEN_AIR) {
		return <Tr.Portal path="parking-area.coverage-and-accessibility-open-air" />;
	} else if (inp === CoverageAndAccessibility.PARKING) {
		return <Tr.Portal path="parking-area.coverage-and-accessibility-parking" />;
	} else if (inp === CoverageAndAccessibility.WITH_SHELTER) {
		return <Tr.Portal path="parking-area.coverage-and-accessibility-with-shelter" />;
	} else {
		return inp;
	}
};
const getInstallationTypeText = (inp: InstallationType): ReactNode => {
	if (inp === InstallationType.POLE) {
		return <Tr.Portal path="parking-area.installation-type-pole" />;
	} else if (inp === InstallationType.WALL) {
		return <Tr.Portal path="parking-area.installation-type-wall" />;
	} else {
		return inp;
	}
};

export interface IAddEditParkingAreaFormProps {
	values?: Partial<ParkArea>;
	onSubmit?: (values: Partial<ParkArea>) => void;
}

export const AddEditParkingAreaForm: FC<IAddEditParkingAreaFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');

	const formik = useFormik<FormFields>({
		initialValues: {
			siteId: '',
			name: '',
			access: '',
			coverageAndAccessibility: '',
			installationType: '',
			powerAvailableForCharging: '',
			parkingSlotsNumber: '',
			currentACInstalled: '',
			currentDCInstalled: '',
			...values,
			floorInstallation: TypeUtils.isDefined(values?.floorInstallation) ? (values?.floorInstallation ? '1' : '0') : undefined
		},
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			siteId: yup.string().required(t('field-required')),
			name: yup.string().required(t('field-required')),
			access: yup.string().optional(),
			coverageAndAccessibility: yup.string().optional(),
			installationType: yup.string().optional(),
			floorInstallation: yup.string().optional(),
			powerAvailableForCharging: yup.string().optional(),
			parkingSlotsNumber: yup.number().optional(),
			currentACInstalled: yup.number().optional(),
			currentDCInstalled: yup.number().optional()
		}),
		onSubmit: (
			{
				name,
				access,
				coverageAndAccessibility,
				installationType,
				floorInstallation,
				powerAvailableForCharging,
				parkingSlotsNumber,
				currentACInstalled,
				currentDCInstalled,
				siteId
			},
			api
		) => {
			onSubmit?.({
				name,
				access,
				coverageAndAccessibility,
				installationType,
				floorInstallation: floorInstallation === '1',
				powerAvailableForCharging: +powerAvailableForCharging,
				parkingSlotsNumber: +parkingSlotsNumber,
				currentACInstalled: +currentACInstalled,
				currentDCInstalled: +currentDCInstalled,
				siteId
			});
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;
	const { data: locations = [] } = useService(() => siteService.getAll(), []);

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: (
						<Tr.Portal
							path={
								values
									? 'parking-area.add-edit-parking-area.update-parking-area'
									: 'parking-area.add-edit-parking-area.add-parking-area'
							}
						/>
					),
					onClick: submitForm,
					'data-testid': 'add-edit-parking-area-button'
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={8}>
					<FormStepQuestion
						title={<Tr.Portal path="parking-area.add-edit-parking-area.enter-parking-area" />}
						titleProps={{ variant: 'h4' }}
					>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="siteId"
									title={t('parking-area.add-edit-parking-area.location')}
									placeholder={t('parking-area.add-edit-parking-area.location')}
									disabled={!!values}
									required
								>
									{locations.map(({ id, name }) => (
										<MenuItem key={`location-${id}`} value={id}>
											{name}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="name"
									title={t('parking-area.add-edit-parking-area.name')}
									placeholder={t('parking-area.add-edit-parking-area.name')}
									required
								/>
							</Grid>

							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="access"
									title={t('parking-area.add-edit-parking-area.access')}
									placeholder={t('parking-area.add-edit-parking-area.access')}
								>
									{Object.values(AccessType).map((type) => (
										<MenuItem key={`access-type-${type}`} value={type}>
											{getAccessTypeText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="coverageAndAccessibility"
									title={t('parking-area.add-edit-parking-area.charging-location')}
									placeholder={t('parking-area.add-edit-parking-area.charging-location')}
								>
									{Object.values(CoverageAndAccessibility).map((type) => (
										<MenuItem key={`coverage-and-accessiblity-${type}`} value={type}>
											{getCoverageAndAccessibilityText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>

							<Grid item xs={6}>
								<FormikTextfield
									select
									formikApi={formik}
									name="installationType"
									title={t('parking-area.add-edit-parking-area.installation-type')}
									placeholder={t('parking-area.add-edit-parking-area.installation-type')}
								>
									{Object.values(InstallationType).map((type) => (
										<MenuItem key={`installation-type-${type}`} value={type}>
											{getInstallationTypeText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={6}>
								<FormikRadioGroup title={t('parking-area.add-edit-parking-area.floor-installation')}>
									<Stack direction="row" spacing={2}>
										<FormikRadio
											formikApi={formik}
											name="floorInstallation"
											value="1"
											formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
										/>
										<FormikRadio
											formikApi={formik}
											name="floorInstallation"
											value="0"
											formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
										/>
									</Stack>
								</FormikRadioGroup>
							</Grid>

							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="powerAvailableForCharging"
									title={t('parking-area.add-edit-parking-area.power-available')}
									placeholder={t('parking-area.add-edit-parking-area.power-available')}
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="parkingSlotsNumber"
									title={t('parking-area.add-edit-parking-area.parking-spot-available')}
									placeholder={t('parking-area.add-edit-parking-area.parking-spot-available')}
									decimal={0}
								/>
							</Grid>

							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="currentACInstalled"
									title={t('parking-area.add-edit-parking-area.ac-installed')}
									placeholder={t('parking-area.add-edit-parking-area.ac-installed')}
									decimal={0}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikNumber
									formikApi={formik}
									name="currentDCInstalled"
									title={t('parking-area.add-edit-parking-area.dc-installed')}
									placeholder={t('parking-area.add-edit-parking-area.dc-installed')}
									decimal={0}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
